// src/components/ModuleSelector.tsx
import React, { useMemo, useState } from "react";
import { ActionIcon, Badge, Box, Button, Group, Menu, Text, Tooltip } from "@mantine/core";
import { FaSlidersH } from "react-icons/fa";
import { FiCheck, FiChevronDown, FiLock } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { changeActiveModuleThunk } from "../designThunk";
import { ModuleSelectionModal } from "./ModuleSelectionModal";
import {
  selectAvailableModulesForCurrentUserInCurrentProject,
  selectIsCurrentUserAccessReady,
  selectVisibleModulesForCurrentProject,
} from "../../projectSelectors";
import { MODULE_ICON_BY_KEY } from "../designModuleIcons";
import { useInternalMsalAccount } from "../../hooks/useInternalMsalAccount";

export const ModuleSelector: React.FC = () => {
  const dispatch = useAppDispatch();

  const activeModule = useAppSelector((s) => s.design.activeModule);
  const availableModuleKeys = useAppSelector(selectAvailableModulesForCurrentUserInCurrentProject);
  const userAccessReady = useAppSelector(selectIsCurrentUserAccessReady);
  const isInternalMsalAccount = useInternalMsalAccount();
  const accessReady = userAccessReady || isInternalMsalAccount;
  const visibleModules = useAppSelector(selectVisibleModulesForCurrentProject);

  const [modalOpen, setModalOpen] = useState(false);

  const activeMod = useMemo(() => {
    return visibleModules.find((mod) => mod.key === activeModule) ?? visibleModules[0];
  }, [activeModule, visibleModules]);

  const hasAccessTo = (key: string) => {
    if (!accessReady) return false;
    if (isInternalMsalAccount) return true;
    // availableModuleKeys er typet som module keys; cast hvis nødvendig
    return (availableModuleKeys as readonly string[]).includes(key);
  };

  const handleSelect = (key: string) => {
    if (!accessReady) return;
    if (!hasAccessTo(key)) return;
    dispatch(changeActiveModuleThunk(key as any));
  };

  const ActiveIcon = activeMod ? MODULE_ICON_BY_KEY[activeMod.key] : null;

  return (
    <Group gap="xs" align="center">
      <Menu withinPortal position="bottom-start" shadow="md">
        <Menu.Target>
          <Button
            size="sm"
            variant="subtle"
            leftSection={ActiveIcon ? <ActiveIcon size={18} /> : undefined}
            rightSection={<FiChevronDown size={14} />}
            w={240}
            justify="space-between"
            styles={{ label: { width: "100%" } }}
          >
            <Text truncate fw={500}>
              {activeMod.title}
            </Text>
          </Button>
        </Menu.Target>

        <Menu.Dropdown w={340}>
          {visibleModules.map((mod) => {
            const isActive = mod.key === activeModule;
            const Icon = MODULE_ICON_BY_KEY[mod.key];

            const canUse = hasAccessTo(mod.key);

            const label = !accessReady
              ? "Laster tilgang…"
              : canUse
                ? mod.description
                : `${mod.description}\n\nDu har ikke tilgang til denne modulen i dette prosjektet.`;

            return (
              <Tooltip key={mod.key} label={label} withArrow position="right" maw={360} multiline>
                <Menu.Item
                  onClick={() => handleSelect(mod.key)}
                  disabled={!canUse}
                  leftSection={
                    isActive ? (
                      <FiCheck size={16} color="var(--mantine-color-blue-6)" />
                    ) : !canUse ? (
                      <FiLock size={16} color="var(--mantine-color-gray-6)" />
                    ) : (
                      <Box w={16} />
                    )
                  }
                  rightSection={
                    !accessReady ? (
                      <Badge size="xs" variant="light" color="gray">
                        Laster
                      </Badge>
                    ) : canUse ? (
                      <Badge size="xs" variant="light">
                        Tilgang
                      </Badge>
                    ) : (
                      <Badge size="xs" variant="light" color="gray">
                        Ingen tilgang
                      </Badge>
                    )
                  }
                >
                  <Group gap="sm" align="center" wrap="nowrap">
                    <Icon size={16} />
                    <Text size="sm" fw={isActive ? 600 : 400} c={canUse ? undefined : "dimmed"}>
                      {mod.title}
                    </Text>
                  </Group>
                </Menu.Item>
              </Tooltip>
            );
          })}
        </Menu.Dropdown>
      </Menu>

      <Tooltip label="Åpne detaljert modulvelger" withArrow position="bottom">
        <ActionIcon
          variant="subtle"
          radius="xl"
          size={34}
          aria-label="Åpne modulvelger"
          onClick={() => setModalOpen(true)}
        >
          <FaSlidersH size={16} />
        </ActionIcon>
      </Tooltip>

      {modalOpen && <ModuleSelectionModal onClose={() => setModalOpen(false)} />}
    </Group>
  );
};
